import React, { useState, useEffect } from 'react';
import {
  Container,
  Tabs,
  Badge,
  Text,
  Paper,
  Group,
  SimpleGrid,
  Card,
  Image,
  Stack,
} from '@mantine/core';
import { Helmet } from 'react-helmet-async';
import { useAppSelector } from '../../store/hooks';
import { useSEO } from '../../hooks/useSEO';
import { galleryService } from '../../services/galleryService';
import { Loader } from '../../components/common/Loader';

const API_URL =
  import.meta.env.VITE_API_BASE_URL ||
  'https://thalvora-exports.onrender.com';

interface Certification {
  id: string;
  name: string;
  issuer: string;
  issueDate: string;
  expiryDate: string;
  image: string;
  description: string;
  order: number;
}

export const CertificationsPage: React.FC = () => {
  const { content } = useAppSelector((state) => state.content);

  const staticCertifications: Certification[] =
    content?.pages?.certifications?.certifications || [];

  const { seo, loading: seoLoading } = useSEO('certifications');

  const [certifications, setCertifications] = useState<
    Certification[]
  >([]);

  const [loading, setLoading] = useState(true);

  const [useFallback, setUseFallback] = useState(false);

  useEffect(() => {
    const loadCertifications = async () => {
      try {
        // 🔥 STEP 1: Load cache instantly
        const cachedCertifications = sessionStorage.getItem(
          'certifications_data'
        );

        if (
          cachedCertifications &&
          cachedCertifications !== 'undefined'
        ) {
          const parsed = JSON.parse(cachedCertifications);

          setCertifications(parsed.certifications || []);
          setUseFallback(parsed.useFallback || false);

          setLoading(false);
        }

        // 🔥 STEP 2: Fetch fresh data in background
        const gallery = await galleryService.getPublicGallery(
          'CERTIFICATIONS'
        );

        const items = gallery.items
          .filter(
            (item) => item.type === 'IMAGE' && item.active
          )
          .map((item) => {
            let meta: any = {};

            try {
              meta = JSON.parse(item.metadata || '{}');
            } catch (e) {}

            return {
              id: item.id,
              name: item.title,
              description: item.description,
              image: item.imageUrl,
              issuer: meta.issuer || '',
              issueDate: meta.issueDate || '',
              expiryDate: meta.expiryDate || '',
              order: meta.order || 0,
            };
          })
          .sort((a, b) => a.order - b.order);

        let finalCertifications: Certification[] = [];
        let fallbackState = false;

        if (items.length > 0) {
          finalCertifications = items;
          fallbackState = false;
        } else {
          // Fallback to static data
          finalCertifications = staticCertifications;
          fallbackState = true;
        }

        setCertifications(finalCertifications);
        setUseFallback(fallbackState);

        // 🔥 STEP 3: Save cache
        sessionStorage.setItem(
          'certifications_data',
          JSON.stringify({
            certifications: finalCertifications,
            useFallback: fallbackState,
          })
        );
      } catch (err) {
        console.error(
          'Failed to fetch certifications gallery:',
          err
        );

        // Fallback on error
        setCertifications(staticCertifications);
        setUseFallback(true);
      } finally {
        setLoading(false);
      }
    };

    loadCertifications();
  }, [staticCertifications]);

  const getImageUrl = (cert: Certification) => {
    let url = cert.image;

    if (!url)
      return '/assets/images/certifications/placeholder.svg';

    if (url.startsWith('http')) return url;

    if (url.startsWith('/assets')) return url;

    // Relative backend path
    return `${API_URL}${
      url.startsWith('/') ? '' : '/'
    }${url}`;
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';

    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  if (seoLoading || loading) {
    return (
      <Container
        size="xl"
        className="flex justify-center items-center py-12"
      >
        <Loader />
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{seo?.metaTitle}</title>

        <meta
          name="description"
          content={seo?.metaDescription}
        />

        <meta
          name="keywords"
          content={seo?.metaKeywords?.join(', ')}
        />

        <meta property="og:title" content={seo?.ogTitle} />

        <meta
          property="og:description"
          content={seo?.ogDescription}
        />

        {seo?.ogImage && (
          <meta
            property="og:image"
            content={seo.ogImage}
          />
        )}

        <meta
          name="twitter:title"
          content={seo?.twitterTitle}
        />

        <meta
          name="twitter:description"
          content={seo?.twitterDescription}
        />

        {seo?.twitterImage && (
          <meta
            name="twitter:image"
            content={seo.twitterImage}
          />
        )}

        {seo?.canonicalUrl && (
          <link
            rel="canonical"
            href={seo.canonicalUrl}
          />
        )}
      </Helmet>

      <Container size="xl">
        <Tabs defaultValue="documents">
          <Tabs.List>
            <Tabs.Tab value="documents">
              Certification Documents
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="documents" pt="xl">
            {/* HEADER */}
            <Paper
              withBorder
              p="md"
              radius="md"
              mb="lg"
            >
              <Group justify="space-between">
                <div>
                  <Text size="lg" fw={600}>
                    Our Certifications
                  </Text>

                  <Text size="sm" c="dimmed">
                    We hold{' '}
                    <strong>
                      {certifications.length}
                    </strong>{' '}
                    international certifications
                  </Text>
                </div>

                <Badge
                  color="primary"
                  size="lg"
                  variant="light"
                >
                  Verified Certifications
                </Badge>
              </Group>
            </Paper>

            {/* CERTIFICATIONS */}
            <Paper withBorder p="md" radius="md">
              <Text size="sm" fw={600} mb="md">
                Certification Documents (
                {certifications.length} documents)
              </Text>

              {useFallback &&
                certifications.length > 0 && (
                  <Text
                    size="xs"
                    c="orange"
                    mb="md"
                  >
                    ℹ️ Using fallback data. Upload
                    certification images to customise.
                  </Text>
                )}

              <SimpleGrid
                cols={{ base: 1, sm: 2, md: 3 }}
                spacing="lg"
              >
                {certifications.map((cert) => (
                  <Card
                    key={cert.id}
                    withBorder
                    padding="lg"
                    radius="md"
                    className="hover:shadow-md transition-shadow"
                  >
                    <Card.Section>
                      <div className="flex justify-center items-center p-6 bg-gray-50 dark:bg-gray-800">
                        <Image
                          src={getImageUrl(cert)}
                          height={120}
                          alt={cert.name}
                          fit="contain"
                          fallbackSrc="/assets/images/certifications/placeholder.svg"
                          loading="lazy"
                        />
                      </div>
                    </Card.Section>

                    <Stack mt="md" gap="xs">
                      <Text
                        fw={600}
                        size="lg"
                        ta="center"
                      >
                        {cert.name}
                      </Text>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>

              {certifications.length === 0 && (
                <Text
                  c="dimmed"
                  ta="center"
                  py={8}
                >
                  No certifications available.
                </Text>
              )}
            </Paper>

            {/* QUALITY */}
            <Paper
              withBorder
              p="md"
              mt="lg"
              radius="md"
              style={{
                backgroundColor:
                  'var(--mantine-color-primary-light)',
              }}
            >
              <Text
                size="sm"
                fw={600}
                mb="xs"
              >
                🏆 Quality Assurance
              </Text>

              <Text size="sm" c="dimmed">
                Our certifications demonstrate our
                commitment to quality, safety, and
                international standards. We are
                regularly audited to ensure compliance
                with all regulatory requirements.
              </Text>

              <Group
                justify="space-between"
                mt="md"
              >
                <Text size="xs" c="dimmed">
                  Certifications:{' '}
                  {certifications.length} | Updated:{' '}
                  {new Date().toLocaleDateString()}
                </Text>
              </Group>
            </Paper>
          </Tabs.Panel>
        </Tabs>
      </Container>
    </>
  );
};